import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, QrCode } from "lucide-react";
import { LanguageSelector } from "./LanguageSelector";
import { RoomShortcut } from "./RoomShortcut";
import { MenuPill } from "./MenuPill";

// Site-wide navigation. External items (Fullboard pages) use plain <a> so
// they open in the SAME TAB — the user must never feel he's "leaving" the
// site. Internal items (our React routes) use <Link> to keep SPA speed.
const SITE_ROOT = "https://www.ilpozzodeigiganti.it";
const BOOKING_URL = `${SITE_ROOT}/booking/prenota-hotel/640_struttura.html?checkFilter=true&date_range=&data_checkin=&data_checkout=&n_camere=1&room_number=1&n_adulti%5B%5D=2&n_bambini%5B%5D=0&trattamento=&send_trattamenti=null`;
const ITEMS = [
  { label: "Chi siamo", href: `${SITE_ROOT}/chi-siamo_1707/`, external: true },
  { label: "Le camere", href: `${SITE_ROOT}/sistemazioni.html`, external: true },
  { label: "La Spa", href: `${SITE_ROOT}/spa_1709/`, external: true },
  { label: "Ristorante", href: `${SITE_ROOT}/ristorante_1710/`, external: true },
  { label: "Servizi", href: `${SITE_ROOT}/servizi_1711/`, external: true },
  { label: "Come arrivare", href: `${SITE_ROOT}/come-arrivare.html`, external: true },
  { label: "Prenota", href: BOOKING_URL, external: true },
  { label: "Shop", to: "/shop", external: false },
  { label: "Asporto", to: "/asporto", external: false },
  { label: "Fidelity", to: "/fidelity", external: false },
  { label: "Contatti", href: `${SITE_ROOT}/contatti/`, external: true },
];

export default function PozzoTopNav({ rightSlot = null, lang = null, setLang = null, onOpenQR = null }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isActive = (it) => it.to && (pathname === it.to || pathname.startsWith(it.to + "/"));

  const homeBtn = <MenuPill />;

  const utilities = (lang && setLang) || onOpenQR ? (
    <div className="flex items-center gap-2" data-testid="topnav-utilities">
      {lang && setLang && <LanguageSelector lang={lang} setLang={setLang} onDark />}
      {onOpenQR && (
        <button
          onClick={onOpenQR}
          className="h-8 w-8 rounded-full bg-[#f7ebdf]/15 ring-1 ring-[#f7ebdf]/30 flex items-center justify-center hover:bg-[#f7ebdf]/25 transition-colors"
          aria-label="QR menu"
          data-testid="topnav-qr"
        >
          <QrCode className="h-4 w-4 text-[#f7ebdf]" />
        </button>
      )}
    </div>
  ) : null;

  return (
    <nav className="bg-[#4a1c17] text-[#f7ebdf] shadow-sm sticky top-0 z-[9999]" data-testid="pozzo-top-nav">
      <div className="max-w-7xl mx-auto px-4 flex items-center gap-4 h-16">
        <a
          href={SITE_ROOT + "/"}
          className="flex-shrink-0 hover:opacity-90 relative z-10"
          data-testid="topnav-logo"
        >
          <div className="h-14 w-14 rounded-full bg-white grid place-items-center overflow-hidden shadow-sm">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_my-menu-app-2/artifacts/fenfexbo_331891EC-9271-4ED9-B0B4-E286F2097757.webp"
              alt="Il Pozzo dei Giganti"
              className="h-full w-full object-contain scale-90"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        </a>
        <RoomShortcut size="h-9 w-9" icon="h-4 w-4" />

        <ul className="hidden lg:flex items-center gap-5 xl:gap-6 flex-1 justify-center">
          {ITEMS.map((it) => (
            <li key={it.label} className="whitespace-nowrap">
              {it.external ? (
                <a href={it.href} className="text-[15px] xl:text-base font-normal hover:opacity-75 transition">{it.label}</a>
              ) : (
                <Link to={it.to} className={`text-[15px] xl:text-base font-normal hover:opacity-75 transition ${isActive(it) ? "underline underline-offset-[6px] decoration-1" : ""}`}>{it.label}</Link>
              )}
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3" data-testid="topnav-right-slot">
          {utilities}
          {rightSlot}
          {homeBtn}
        </div>

        <div className="ml-auto lg:hidden flex items-center gap-2">
          {utilities}
          {rightSlot}
          {homeBtn}
          <button
            onClick={() => setOpen((v) => !v)}
            className="p-2 rounded-md hover:bg-white/10"
            aria-label="Menu"
            data-testid="topnav-burger"
          >
            {open ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="lg:hidden bg-[#3a1b18] border-t border-white/10 max-h-[80vh] overflow-y-auto">
          <ul className="py-2">
            {ITEMS.map((it) => (
              <li key={it.label}>
                {it.external ? (
                  <a
                    href={it.href}
                    onClick={() => setOpen(false)}
                    className="block px-6 py-2.5 text-[15px] hover:bg-white/10"
                  >
                    {it.label}
                  </a>
                ) : (
                  <Link
                    to={it.to}
                    onClick={() => setOpen(false)}
                    className={`block px-6 py-2.5 text-[15px] ${isActive(it) ? "bg-white/15" : "hover:bg-white/10"}`}
                  >
                    {it.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
}
